import type { LoaderFunctionArgs, MetaFunction } from "react-router";
import { useLoaderData } from "react-router";
import { SITE_NAME, API_BASE } from "@/lib/constants";
import { safeFetchJson } from "@/lib/fetch-json";
import type { BrandProfile } from "@/lib/api";
import BrandPage from "@/pages/BrandPage";

export async function loader({ params }: LoaderFunctionArgs) {
  const domain = params.domain!;
  const brand = await safeFetchJson<BrandProfile>(`${API_BASE}/describe/${domain}`, {
    timeoutMs: 3000,
  });
  return { domain, brand };
}

export const meta: MetaFunction<typeof loader> = ({ data }) => {
  const domain = data?.domain ?? "";
  const brand = data?.brand;
  const origin = "https://loadlogo.com";

  if (brand && !brand.error) {
    const name = brand.name || domain;
    const description = `Download the ${name} logo in SVG, PNG, and WebP formats. ${brand.description ? brand.description.slice(0, 120) : `Get the ${name} logo via the ${SITE_NAME} API.`}`;
    return [
      { title: `${name} Logo | Download SVG & PNG | ${SITE_NAME}` },
      { name: "description", content: description },
      { property: "og:title", content: `${name} Logo | Download SVG & PNG | ${SITE_NAME}` },
      { property: "og:description", content: description },
      { property: "og:type", content: "website" },
      { property: "og:image", content: `${origin}/og/brand/${domain}` },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { property: "og:url", content: `${origin}/logo/${domain}` },
      { property: "og:site_name", content: SITE_NAME },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: `${name} Logo | Download SVG & PNG | ${SITE_NAME}` },
      { name: "twitter:description", content: description },
      { name: "twitter:image", content: `${origin}/og/brand/${domain}` },
      { tagName: "link", rel: "canonical", href: `${origin}/logo/${domain}` },
      {
        "script:ld+json": JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ImageObject",
          name: `${name} Logo`,
          contentUrl: `https://img.loadlogo.com/${domain}`,
          thumbnailUrl: `https://img.loadlogo.com/${domain}?size=64`,
        }),
      },
    ];
  }

  return [
    { title: `${domain} Logo | ${SITE_NAME}` },
    {
      name: "description",
      content: `Download the ${domain} logo in SVG, PNG, and WebP formats via the ${SITE_NAME} API.`,
    },
    { property: "og:title", content: `${domain} Logo | ${SITE_NAME}` },
    {
      property: "og:description",
      content: `Download the ${domain} logo in SVG, PNG, and WebP formats via the ${SITE_NAME} API.`,
    },
    { property: "og:type", content: "website" },
    { property: "og:image", content: `${origin}/og/brand/${domain}` },
    { property: "og:image:width", content: "1200" },
    { property: "og:image:height", content: "630" },
    { property: "og:url", content: `${origin}/logo/${domain}` },
    { property: "og:site_name", content: SITE_NAME },
    { name: "twitter:card", content: "summary_large_image" },
    { tagName: "link", rel: "canonical", href: `${origin}/logo/${domain}` },
  ];
};

export default function BrandRoute() {
  const { brand } = useLoaderData<typeof loader>();
  return <BrandPage serverData={brand} />;
}
